import React from 'react'
import { Button, Col, Form, Input, message, Row, Select, Spin, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import SearchField from '../../../common/searchFields/SearchField'
import { SEARCH_PATIENT_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'
import { isMobile } from 'react-device-detect'
import { familyRelations } from '@dentolize/clinic-mobile/src/shared/utils/helpers'
import { useMutation, useQuery } from '@apollo/client'
import DELETE_FAMILY_RELATION from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/patient/deleteFamilyRelation'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import { useLoginModal } from '../../../../context/loginModalContext'
import * as PropTypes from 'prop-types'
import { PATIENT_DETAILS } from '@dentolize/clinic-mobile/src/shared/store/queries/patientQueries'
import { showCustomConfirm } from '../../appointments/Calendar/calendarHelpers'

const PatientFamilyForm = ({ getFieldValue, selectedPatient }) => {
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()

  const [deleteRelation, { loading }] = useMutation(DELETE_FAMILY_RELATION, {
    refetchQueries: [{ query: PATIENT_DETAILS, variables: { patient: selectedPatient.id } }],
    awaitRefetchQueries: true
  })

  const { data } = useQuery(PATIENT_DETAILS, {
    variables: { patient: selectedPatient.id },
    skip: !selectedPatient.id
  })

  const handleDelete = async (remove, field, index) => {
    const value = getFieldValue(['family', index])
    if (value.id) {
      try {
        const confirm = await showCustomConfirm(t, t('common.sureDelete'), null, 'error')

        if (!confirm) return

        await deleteRelation({ variables: { patientFamily: value.id } })
        message.success(t('common.deletedSuccess'))
      } catch (e) {
        showAuthError(e, t, setLoginModalVisible)
        return
      }
    }
    remove(field.name)
  }

  return (
    <Col span={24}>
      <Spin spinning={loading}>
        <Form.List name="family">
          {(fields, { add, remove }) => (
            <div>
              <div className="divider-section has-button">
                <Button
                  data-testid="patientForm-newFamily"
                  icon={<PlusOutlined />}
                  type="text"
                  disabled={fields.length > 49}
                  ghost
                  onClick={() => add({})}
                >
                  <Typography.Text strong>{t('app.familyMembers')}</Typography.Text>
                </Button>
              </div>
              {fields.map((field, index) => (
                <Row key={index} gutter={16}>
                  <Col md={10} xs={24}>
                    <Form.Item hidden name={[index, 'id']}>
                      <Input data-testid={`patientForm-familyId-${index}`} />
                    </Form.Item>
                    <SearchField
                      rules={[{ required: true, message: t('app.fieldRequired') }]}
                      GET_QUERY={SEARCH_PATIENT_QUERY}
                      label={t('common.selectPatient')}
                      fieldName={[index, 'patient']}
                      dataName="searchPatients"
                      disabled={index < data?.patientDetails?.primaryFamily?.length}
                    />
                  </Col>
                  <Col md={10} xs={16}>
                    <Form.Item
                      label={t('patients.relationship')}
                      name={[index, 'relation']}
                      rules={[{ required: true, message: t('app.fieldRequired') }]}
                    >
                      <Select
                        data-testid={`patientForm-familyRelation-${index}`}
                        className="fixArrowSelection"
                        showSearch
                        disabled={index < data?.patientDetails?.primaryFamily?.length}
                        showAction={['click', isMobile ? '' : 'focus']}
                        filterOption={(input, option) => option['data-search']?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                      >
                        {familyRelations.map(value => (
                          <Select.Option value={value} key={value} data-search={t(`relations.${value}`)}>
                            {t(`relations.${value}`)}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col md={4} xs={8}>
                    <Form.Item label={' '} colon={false}>
                      <Button
                        data-testid={`patientForm-deleteFamily-${index}`}
                        icon={<DeleteOutlined />}
                        type="primary"
                        danger
                        onClick={() => handleDelete(remove, field, index)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </div>
          )}
        </Form.List>
      </Spin>
    </Col>
  )
}

PatientFamilyForm.propTypes = {
  getFieldValue: PropTypes.func.isRequired,
  selectedPatient: PropTypes.object
}

export default PatientFamilyForm
